import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  styled
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, addDays, differenceInMinutes, parseISO } from 'date-fns';
import { tr as trLocale, enUS } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import firebaseDataService from '../../services/FirebaseDataService';
import * as shiftDataService from '../../services/migration/shiftDataService';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import FilterToggle from './FilterToggle';
import { useTranslation } from 'react-i18next';

interface Personnel {
  id: string;
  name: string;
  branchesId?: string | Record<string, any> | null;
}

interface ShiftRecord {
  date: string;
  girisZamani: number;
  cikisZamani: number | null;
  vardiyaAdi: string;
  totalWorkMinutes: number;
  isNextDayExit?: boolean;
  isMissing?: boolean;
  dayOfWeek?: string;
}

interface PersonnelWorkData {
  id: string;
  name: string;
  records: ShiftRecord[];
  totals: {
    entriesCount: number;
    exitsCount: number;
    totalWorkMinutes: number;
  };
}

interface ToplamSureProps {
  branchId?: string;
  isManager?: boolean;
}

// Personel özet kartı için styled component
const SummaryCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

// Kompakt satır için styled component
const CompactRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMissing',
})<{ isMissing?: boolean }>(({ theme, isMissing }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: isMissing ? 'rgba(229, 115, 115, 0.1)' : 'transparent',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const ToplamSure: React.FC<ToplamSureProps> = ({ branchId, isManager = false }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const { userDetails } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [personnelList, setPersonnelList] = useState<Personnel[]>([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(addDays(new Date(), 7));
  const [workData, setWorkData] = useState<PersonnelWorkData[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [selectAllPersonnel, setSelectAllPersonnel] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  
  // Filtre görünürlüğü için state - Varsayılan kapalı
  const [showFilters, setShowFilters] = useState(false);

  // Personel listesini yükle
  useEffect(() => {
    const fetchPersonnel = async () => {
      if (!userDetails?.companyId) return;

      try {
        // Cloud Functions üzerinden personel çek
        const personnelResult = await firebaseDataService.getPersonnel(userDetails.companyId);
        
        // Personel verilerini objeye dönüştür
        let data: Record<string, any> = {};
        if (Array.isArray(personnelResult)) {
          personnelResult.forEach((p: any) => { data[p.id] = p; });
        } else if (personnelResult) {
          data = personnelResult as Record<string, any>;
        }
        
        if (Object.keys(data).length > 0) {
          let personnelArray: Personnel[] = Object.keys(data)
            .filter(id => {
              // Silinen personelleri filtrele (isDeleted = true olanları hariç tut)
              const isDeleted = data[id].isDeleted === true;
              return !isDeleted;
            })
            .map(id => ({
              id,
              name: data[id].name || `${data[id].firstName || ''} ${data[id].lastName || ''}`.trim() || id,
              branchesId: data[id].branchesId || data[id].branchId || data[id].branch || null
            }));
          
          // Şube yöneticisi ise sadece kendi şubesindeki personeli göster
          if (isManager && branchId) {
            console.log(`Toplam Süre: Sadece şube ID'si ${branchId} olan personel filtreleniyor`);
            personnelArray = personnelArray.filter(person => {
              // Personelin şube bilgisi veritabanında farklı alanlarda olabilir
              const personBranchId = person.branchesId;
              
              if (!personBranchId) {
                return false;
              }
              
              if (typeof personBranchId === 'string') {
                // Şube ID'si string ise doğrudan karşılaştır
                return personBranchId === branchId;
              } else if (typeof personBranchId === 'object') {
                // 'id' özelliği var mı kontrol et
                if ('id' in personBranchId && personBranchId.id === branchId) {
                  return true;
                }
                // Objenin anahtarları arasında branchId'yi içeriyor olabilir
                return Object.keys(personBranchId).includes(branchId);
              }
              
              return false;
            });
            
            console.log(`Toplam Süre: Filtrelenen personel sayısı: ${personnelArray.length}`);
          }
          
          setPersonnelList(personnelArray);
        }
      } catch (error) {
        console.error("Personel listesi yüklenirken hata:", error);
      }
    };

    fetchPersonnel();
  }, [userDetails, isManager, branchId]);

  // Tüm personeli seç
  const handleSelectAllPersonnel = (checked: boolean) => {
    setSelectAllPersonnel(checked);
    if (checked) {
      setSelectedPersonnel([...personnelList]);
    } else {
      setSelectedPersonnel([]);
    }
  };

  // Personel çalışma verilerini getir
  const fetchWorkData = async () => {
    if (!userDetails?.companyId || !startDate || !endDate || selectedPersonnel.length === 0) {
      return;
    }

    setLoading(true);
    setIsFiltered(true);

    try {
      // Tarih karşılaştırması için tarih nesneleri oluştur
      const startDateObj = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endDateObj = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      
      const personnelWorkData: PersonnelWorkData[] = [];
      
      // Vardiya kayıtlarını SQL'den (gts_hr_api) çek ve RTDB vardiyaListesi şekline dönüştür
      const fromIso = format(startDateObj, 'yyyy-MM-dd');
      const toIso = format(endDateObj, 'yyyy-MM-dd');

      const shiftsRaw = await shiftDataService.getShifts(userDetails.companyId);
      const nextDayMap: Record<string, boolean> = {};
      shiftsRaw.forEach((s) => { nextDayMap[String(s.id)] = !!s.isNextDayExit; });

      const entries: any[] = [];
      let pageNo = 1;
      const pageSize = 200;
      for (let i = 0; i < 100; i++) {
        const res = await shiftDataService.getAttendanceList({ from: fromIso, to: toIso, page: pageNo, pageSize });
        entries.push(...res.items);
        if (entries.length >= res.total || res.items.length === 0) break;
        pageNo++;
      }

      // Gün+personel başına oturum LISTESI (dual → çoklu oturum). Single tek elemanlı liste.
      const allShiftData: Record<string, Record<string, any[]>> = {};
      for (const e of entries) {
        const [y, m, d] = String(e.attendanceDate).split('-');
        const ddmm = `${d}-${m}-${y}`;
        if (!allShiftData[ddmm]) allShiftData[ddmm] = {};
        if (!allShiftData[ddmm][e.personnelUid]) allShiftData[ddmm][e.personnelUid] = [];
        allShiftData[ddmm][e.personnelUid].push({
          girisZamani: e.checkInTime ? new Date(e.checkInTime).getTime() : 0,
          cikisZamani: e.checkOutTime ? new Date(e.checkOutTime).getTime() : null,
          vardiyaAdi: e.shiftName,
          vardiyaBilgisi: { isNextDayExit: e.shiftId != null ? !!nextDayMap[String(e.shiftId)] : false },
          attendanceMode: e.attendanceMode === 'dual' ? 'dual' : 'single',
          anomaly: e.anomaly || null,
          workedMinutesServer: typeof e.workedMinutes === 'number' ? e.workedMinutes : null,
        });
      }

      for (const personnel of selectedPersonnel) {
        if (allShiftData) {
          const personnelRecords: ShiftRecord[] = [];
          let entriesCount = 0;
          let exitsCount = 0;
          let totalWorkMinutes = 0;
          
          // Tarih aralığındaki her gün için kontrol etmek üzere tarih listesi oluştur
          const allDatesInRange: string[] = [];
          const currentDate = new Date(startDateObj);
          
          // Tüm tarihleri dd-MM-yyyy formatında listeye ekle
          while (currentDate <= endDateObj) {
            const dateStr = format(currentDate, 'dd-MM-yyyy');
            allDatesInRange.push(dateStr);
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          // Var olan kayıtları işle
          const existingRecordDates: string[] = [];
          
          // Tarih bazlı kayıtları işle
          Object.keys(allShiftData).forEach(date => {
            // Tarih string'ini Date nesnesine çevir (dd-MM-yyyy)
            const dateParts = date.split('-');
            if (dateParts.length !== 3) return; // Geçersiz tarih formatı
            
            const day = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10) - 1; // JavaScript ayları 0'dan başlar
            const year = parseInt(dateParts[2], 10);
            
            const currentDateObj = new Date(year, month, day);
            
            // Tarih filtreleme (Date nesneleri ile karşılaştırma)
            if (currentDateObj >= startDateObj && currentDateObj <= endDateObj) {
              const dateData = allShiftData[date];
              
              // Personele ait kayıt(lar) varsa — dual'de günde birden çok oturum olabilir.
              const dayList: any[] = dateData[personnel.id] || [];
              if (dayList.length > 0) {
                // Oturumları giriş (yoksa çıkış) zamanına göre sırala
                const sessions = [...dayList].sort((a, b) => {
                  const at = a.girisZamani || a.cikisZamani || 0;
                  const bt = b.girisZamani || b.cikisZamani || 0;
                  return at - bt;
                });
                const multi = sessions.length > 1;

                // Haftanın gününü hesapla
                const recordDate = new Date(year, month, day);
                const dayOfWeek = format(recordDate, 'EEEE', { locale: isEnglish ? enUS : trLocale });
                const dayLabel = dayOfWeek.charAt(0).toLocaleUpperCase('tr-TR') + dayOfWeek.slice(1);

                sessions.forEach((record, sIdx) => {
                  // Giriş/çıkış sayacını güncelle
                  if (record.girisZamani) entriesCount++;
                  if (record.cikisZamani) exitsCount++;

                  // Ertesi gün çıkış bilgisi
                  const isNextDayExit = !!(record.vardiyaBilgisi && record.vardiyaBilgisi.isNextDayExit);

                  // Toplam çalışma süresi (dakika): dual → sunucu workedMinutes (tam oturum),
                  // yoksa/single → tarih-saat farkı (ertesi gün geçişi düzeltmeli).
                  let workMinutes = 0;
                  if (record.attendanceMode === 'dual') {
                    // Dual: yalnızca tam oturum (giriş+çıkış, anomali değil) süre sayar.
                    if (record.girisZamani && record.cikisZamani && !record.anomaly) {
                      workMinutes = typeof record.workedMinutesServer === 'number'
                        ? record.workedMinutesServer
                        : Math.max(0, Math.floor((record.cikisZamani - record.girisZamani) / (1000 * 60)));
                    }
                  } else if (record.girisZamani && record.cikisZamani) {
                    const girisDate = new Date(record.girisZamani);
                    const cikisDate = new Date(record.cikisZamani);
                    if (isNextDayExit) {
                      if (cikisDate.getHours() < girisDate.getHours()) {
                        cikisDate.setDate(cikisDate.getDate() + 1);
                      }
                    }
                    workMinutes = Math.max(0, Math.floor((cikisDate.getTime() - girisDate.getTime()) / (1000 * 60)));
                  }

                  totalWorkMinutes += workMinutes;

                  // Çoklu oturumda vardiya adına "(Oturum n)" ekle
                  let shiftLabel = record.vardiyaAdi || tr('Bilinmeyen Vardiya', 'Unknown Shift');
                  if (multi) {
                    shiftLabel = `${shiftLabel} (${tr('Oturum', 'Session')} ${sIdx + 1})`;
                  }

                  personnelRecords.push({
                    date,
                    girisZamani: record.girisZamani,
                    cikisZamani: record.cikisZamani,
                    vardiyaAdi: shiftLabel,
                    totalWorkMinutes: workMinutes,
                    isNextDayExit: isNextDayExit,
                    dayOfWeek: dayLabel,
                  });
                });

                // Bu tarihin işlendiğini kaydet
                existingRecordDates.push(date);
              }
            }
          });
          
          // Gelmediği günleri tespit et ve ekle
          allDatesInRange.forEach(date => {
            if (!existingRecordDates.includes(date)) {
              // Tarih nesnesi oluştur
              const dateParts = date.split('-');
              const day = parseInt(dateParts[0], 10);
              const month = parseInt(dateParts[1], 10) - 1;
              const year = parseInt(dateParts[2], 10);
              const dateObj = new Date(year, month, day);
              
              // Haftanın gününü hesapla
              const dayOfWeek = format(dateObj, 'EEEE', { locale: isEnglish ? enUS : trLocale });
              
              // Gelmediği gün kaydını ekle
              personnelRecords.push({
                date,
                girisZamani: 0,
                cikisZamani: null,
                vardiyaAdi: tr('Gelmedi', 'Absent'),
                totalWorkMinutes: 0,
                isMissing: true,
                dayOfWeek: dayOfWeek.charAt(0).toLocaleUpperCase('tr-TR') + dayOfWeek.slice(1)
              });
            }
          });
          
          // Tarihe göre sırala
          personnelRecords.sort((a, b) => {
            // Tarihleri parçalara ayır ve tarih nesneleri oluştur
            const aDateParts = a.date.split('-');
            const bDateParts = b.date.split('-');
            
            const aDate = new Date(
              parseInt(aDateParts[2], 10),
              parseInt(aDateParts[1], 10) - 1,
              parseInt(aDateParts[0], 10)
            );
            
            const bDate = new Date(
              parseInt(bDateParts[2], 10),
              parseInt(bDateParts[1], 10) - 1,
              parseInt(bDateParts[0], 10)
            );
            
            // Tarih nesneleri ile karşılaştır
            return aDate.getTime() - bDate.getTime();
          });
          
          // Personel veri nesnesini ekle
          personnelWorkData.push({
            id: personnel.id,
            name: personnel.name,
            records: personnelRecords,
            totals: {
              entriesCount,
              exitsCount,
              totalWorkMinutes
            }
          });
        }
      }
      
      setWorkData(personnelWorkData);
    } catch (error) {
      console.error("Çalışma verileri yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  // Dakikayı saat:dakika formatına dönüştür
  const formatMinutesToHours = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Unix timestamp'i saat:dakika formatına dönüştür
  const formatTime = (timestamp: number | null) => {
    if (!timestamp) return '--:--';
    const date = new Date(timestamp);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };
  
  // Dakika cinsinden çalışma süresini hesaplama (GirisCikisRaporlari ile aynı yöntem)
  const calculateWorkingMinutes = (checkInTime: string, checkOutTime: string): number => {
    // Eğer giriş veya çıkış yapılmamışsa hesaplanmaz
    if (checkInTime === '--:--' || checkOutTime === '--:--') {
      return 0;
    }
    
    try {
      // Saat ve dakikaları parse et
      const [inHour, inMinute] = checkInTime.split(':').map(Number);
      const [outHour, outMinute] = checkOutTime.split(':').map(Number);
      
      // Dakika cinsinden toplam süre hesapla
      let totalMinutes = (outHour * 60 + outMinute) - (inHour * 60 + inMinute);
      
      // Çıkış saati giriş saatinden önceyse (gün değişimi olmuş)
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60; // 24 saat ekle
      }
      
      return totalMinutes;
    } catch (error) {
      console.error('Çalışma dakikası hesaplama hatası:', error);
      return 0;
    }
  };

  // Excel'e veri aktarma olayını dinle
  useEffect(() => {
    const handleExportToExcel = (event: CustomEvent) => {
      const { worksheet } = event.detail;
      
      // Excel stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        },
        alignment: { horizontal: 'center' as const }
      };
      
      const totalRowStyle = {
        font: { bold: true },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF5F5F5' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      const normalCellStyle = {
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      const titleStyle = {
        font: { bold: true, size: 14 }
      };
      
      // Başlık satırını ekle
      worksheet.addRow([tr('Personel Çalışma Süreleri Raporu', 'Personnel Working Hours Report')]);
      worksheet.mergeCells('A1:E1');
      const titleRow = worksheet.getRow(1);
      titleRow.font = { bold: true, size: 16 };
      titleRow.alignment = { horizontal: 'center' };
      
      // Tarih bilgisi
      const today = new Date();
      const dateStr = format(today, 'dd.MM.yyyy');
      worksheet.addRow([`${tr('Rapor Tarihi', 'Report Date')}: ${dateStr}`]);
      worksheet.mergeCells('A2:E2');
      
      // İlk iki satıra boşluk bırak
      worksheet.addRow([]);
      
      // Her personel için ayrı tablo oluştur
      let currentRow = 4;
      
      workData.forEach((personnel, index) => {
        // Personel başlığı ekle
        worksheet.addRow([personnel.name]);
        worksheet.mergeCells(`A${currentRow}:E${currentRow}`);
        const personelRow = worksheet.getRow(currentRow);
        personelRow.font = titleStyle.font;
        personelRow.height = 30;
        personelRow.alignment = { vertical: 'middle' };
        currentRow++;
        
        // Tablo sütun başlıkları
        const headerRow = worksheet.addRow([
          tr('Tarih', 'Date'),
          tr('Gün', 'Day'),
          tr('Giriş Saati', 'Start Time'),
          tr('Çıkış Saati', 'End Time'),
          tr('Toplam Çalışma', 'Total Work'),
          tr('Vardiya', 'Shift')
        ]);
        
        // Başlık stilini uygula
        headerRow.eachCell((cell: any) => {
          cell.style = headerStyle;
          cell.alignment = { horizontal: 'center' };
        });
        headerRow.height = 25;
        currentRow++;
        
        // Sütun genişliklerini ayarla
        worksheet.getColumn('A').width = 15;
        worksheet.getColumn('B').width = 15;
        worksheet.getColumn('C').width = 15;
        worksheet.getColumn('D').width = 18;
        worksheet.getColumn('E').width = 20;
        
        if (personnel.records.length === 0) {
          // Kayıt yoksa bilgi mesajı
          const noDataRow = worksheet.addRow([tr('Bu personel için kayıt bulunamadı', 'No records found for this personnel')]);
          worksheet.mergeCells(`A${currentRow}:E${currentRow}`);
          noDataRow.alignment = { horizontal: 'center' };
          currentRow++;
        } else {
          // Personel kayıtlarını ekle
          personnel.records.forEach(record => {
            const dataRow = worksheet.addRow([
              record.date,
              record.dayOfWeek,
              formatTime(record.girisZamani),
              formatTime(record.cikisZamani) + (record.isNextDayExit ? ` (${tr('Ertesi Gün', 'Next Day')})` : ''),
              formatMinutesToHours(record.totalWorkMinutes),
              record.vardiyaAdi
            ]);
            
            // Hücre stillerini uygula
            dataRow.eachCell((cell: any) => {
              cell.style = normalCellStyle;
              cell.alignment = { horizontal: 'center' };
            });
            
            currentRow++;
          });
          
          // Toplam satırı ekle
          const totalRow = worksheet.addRow([
            tr('TOPLAM', 'TOTAL'),
            '',
            `${personnel.totals.entriesCount} ${tr('Giriş', 'Check-In')}`,
            `${personnel.totals.exitsCount} ${tr('Çıkış', 'Check-Out')}`,
            formatMinutesToHours(personnel.totals.totalWorkMinutes),
            ''
          ]);
          
          // Toplam satırı stilini uygula
          totalRow.eachCell((cell: any) => {
            cell.style = totalRowStyle;
            cell.alignment = { horizontal: 'center' };
          });
          totalRow.height = 25;
          currentRow++;
        }
        
        // Tablolar arasında boşluk bırak
        worksheet.addRow([]);
        currentRow++;
      });
    };
    
    // Event listener'ı ekle
    window.addEventListener('export-toplamsure-to-excel', handleExportToExcel as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('export-toplamsure-to-excel', handleExportToExcel as EventListener);
    };
  }, [workData]);

  // Scroll olayını dinle
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {tr('Toplam Çalışma Süreleri', 'Total Working Durations')}
        </Typography>
      </Box>
      
      {/* Filtre Toggle Bileşeni */}
      <FilterToggle
        open={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isEnglish ? enUS : trLocale}>
          <Grid container spacing={2}>
            {/* Personel Seçimi */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="personnel-selector"
                options={personnelList}
                value={selectedPersonnel}
                onChange={(_, newValue) => {
                  setSelectedPersonnel(newValue);
                  setSelectAllPersonnel(newValue.length === personnelList.length);
                }}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label={tr('Personel Seçin', 'Select Personnel')}
                    placeholder={tr('Personel ara...', 'Search personnel...')}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      )
                    }}
                  />
                )}
                ref={autocompleteRef}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                ListboxProps={{ style: { maxHeight: '200px' } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllPersonnel}
                    onChange={(e) => handleSelectAllPersonnel(e.target.checked)}
                    size="small"
                  />
                }
                label={tr('Tüm personeli seç', 'Select all personnel')}
              />
            </Grid>
            
            {/* Tarih Aralığı Seçimi */}
            <Grid item xs={12} md={5}>
              <DatePicker
                label={tr('Başlangıç Tarihi', 'Start Date')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    variant: "outlined"
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={5}>
              <DatePicker
                label={tr('Bitiş Tarihi', 'End Date')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    variant: "outlined"
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchWorkData}
                disabled={loading || selectedPersonnel.length === 0 || !startDate || !endDate}
                fullWidth
                sx={{ height: '100%' }}
              >
                {loading ? tr('Yükleniyor...', 'Loading...') : tr('Filtrele', 'Filter')}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </FilterToggle>
      
      {/* Sonuçlar Bölümü */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : !isFiltered ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          p: 5, 
          bgcolor: '#f8f9fa', 
          borderRadius: 2,
          minHeight: '200px' 
        }}>
          <FilterListIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" align="center">
            {tr('Personel ve tarih aralığı seçerek çalışma sürelerini görüntüleyebilirsiniz.', 'You can view working durations by selecting personnel and a date range.')}
          </Typography>
        </Box>
      ) : workData.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          p: 4, 
          bgcolor: '#f8f9fa', 
          borderRadius: 2 
        }}>
          <Typography variant="body1" color="text.secondary">
            {tr('Seçili tarih aralığında çalışma kaydı bulunamadı.', 'No work records found in the selected date range.')}
          </Typography>
        </Box>
      ) : isMobile ? (
        /* Mobil Kompakt Görünüm */
        <Box sx={{ pb: 2 }}>
          {workData.map((personnel) => (
            <SummaryCard key={personnel.id}>
              {/* Personel Başlığı ve Özet */}
              <CardContent sx={{ pb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  {personnel.name}
                </Typography>
                
                {/* Özet Bilgiler */}
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={3}>
                    <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                        {tr('Giriş', 'In')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {personnel.totals.entriesCount}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                        {tr('Çıkış', 'Out')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="info.main">
                        {personnel.totals.exitsCount}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', borderRadius: 1, bgcolor: 'primary.50' }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                        {tr('Toplam', 'Total')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {formatMinutesToHours(personnel.totals.totalWorkMinutes)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', borderRadius: 1, borderColor: personnel.records.filter(r => r.isMissing).length > 0 ? 'error.main' : 'divider' }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                        {tr('Gelmedi', 'Absent')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color={personnel.records.filter(r => r.isMissing).length > 0 ? 'error.main' : 'text.secondary'}>
                        {personnel.records.filter(r => r.isMissing).length}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
              
              {/* Kompakt Tablo Başlığı */}
              <Box sx={{ 
                display: 'flex', 
                bgcolor: 'grey.100', 
                px: 1.5, 
                py: 0.75,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="caption" fontWeight="bold" sx={{ flex: 2, fontSize: '0.7rem' }}>
                  {tr('Tarih', 'Date')}
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ flex: 3, textAlign: 'center', fontSize: '0.7rem' }}>
                  {tr('Saat', 'Time')}
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ flex: 1.5, textAlign: 'right', fontSize: '0.7rem' }}>
                  {tr('Toplam', 'Total')}
                </Typography>
              </Box>
              
              {/* Kompakt Satırlar */}
              {personnel.records.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {tr('Kayıt bulunamadı', 'No records found')}
                  </Typography>
                </Box>
              ) : (
                personnel.records.map((record, idx) => (
                  <CompactRow key={`${record.date}_${idx}`} isMissing={record.isMissing}>
                    {/* Tarih + Gün */}
                    <Box sx={{ flex: 2 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                        {record.dayOfWeek?.substring(0, 3)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {record.date.substring(0, 5)}
                      </Typography>
                    </Box>
                    
                    {/* Giriş → Çıkış */}
                    <Box sx={{ flex: 3, textAlign: 'center' }}>
                      {record.isMissing ? (
                        <Chip 
                          label={tr('Gelmedi', 'Absent')} 
                          size="small"
                          color="error"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formatTime(record.girisZamani)}
                          <span style={{ margin: '0 4px', color: '#999' }}>→</span>
                          {formatTime(record.cikisZamani)}
                          {record.isNextDayExit && record.cikisZamani && (
                            <Typography component="span" variant="caption" color="info.main" sx={{ ml: 0.5, fontSize: '0.6rem' }}>
                              +1
                            </Typography>
                          )}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Toplam */}
                    <Box sx={{ flex: 1.5, textAlign: 'right' }}>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold" 
                        color={record.isMissing ? 'error.main' : 'success.main'}
                        sx={{ fontSize: '0.85rem' }}
                      >
                        {record.isMissing ? '00:00' : formatMinutesToHours(record.totalWorkMinutes)}
                      </Typography>
                    </Box>
                  </CompactRow>
                ))
              )}
            </SummaryCard>
          ))}
        </Box>
      ) : (
        /* Desktop Tablo Görünümü */
        <Box>
          {workData.map((personnel) => (
            <Box key={personnel.id} mb={4}>
              <Typography variant="h6" component="h3" gutterBottom>
                {personnel.name}
              </Typography>
              
              {personnel.records.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {tr('Bu personel için seçilen tarih aralığında kayıt bulunamadı', 'No records found for this personnel in the selected date range')}
                </Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{tr('Tarih', 'Date')}</TableCell>
                        <TableCell>{tr('Gün', 'Day')}</TableCell>
                        <TableCell>{tr('Giriş Saati', 'Start Time')}</TableCell>
                        <TableCell>{tr('Çıkış Saati', 'End Time')}</TableCell>
                        <TableCell>{tr('Toplam Çalışma', 'Total Work')}</TableCell>
                        <TableCell>{tr('Vardiya', 'Shift')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {personnel.records.map((record, idx) => (
                        <TableRow 
                          key={`${record.date}_${idx}`}
                          sx={{ 
                            backgroundColor: record.isMissing ? 'rgba(229, 115, 115, 0.1)' : 'inherit',
                            '&:hover': { 
                              backgroundColor: record.isMissing ? 'rgba(229, 115, 115, 0.2)' : 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <TableCell>
                            {record.date}
                          </TableCell>
                          <TableCell>
                            {record.dayOfWeek}
                          </TableCell>
                          <TableCell sx={{ color: record.isMissing ? 'error.main' : 'inherit' }}>
                            {record.isMissing ? tr('Gelmedi', 'Absent') : formatTime(record.girisZamani)}
                          </TableCell>
                          <TableCell sx={{ color: record.isMissing ? 'error.main' : 'inherit' }}>
                            {record.isMissing ? tr('Gelmedi', 'Absent') : (
                              <>
                                {formatTime(record.cikisZamani)}
                                {record.isNextDayExit && record.cikisZamani && (
                                  <Chip 
                                    label={tr('Ertesi Gün', 'Next Day')} 
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ 
                                      ml: 0.5, 
                                      height: 16, 
                                      fontSize: '9px',
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: record.isMissing ? 'error.main' : 'inherit' }}>
                            {record.isMissing ? '00:00' : formatMinutesToHours(record.totalWorkMinutes)}
                          </TableCell>
                          <TableCell sx={{ color: record.isMissing ? 'error.main' : 'inherit' }}>
                            {record.isMissing ? (
                              <Chip 
                                label={tr('Gelmedi', 'Absent')} 
                                size="small"
                                color="error"
                                sx={{ fontSize: '11px', height: 24 }}
                              />
                            ) : record.vardiyaAdi}
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Toplam Satırı */}
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                          {tr('TOPLAM', 'TOTAL')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {personnel.totals.entriesCount} {tr('Giriş', 'Check-In')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {personnel.totals.exitsCount} {tr('Çıkış', 'Check-Out')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {formatMinutesToHours(personnel.totals.totalWorkMinutes)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {personnel.records.filter(r => r.isMissing).length} {tr('Gün Gelmedi', 'Days Absent')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              <Divider sx={{ mt: 4 }} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ToplamSure; 